/**
 * Member Group Types
 * 
 * Types สำหรับระบบจัดกลุ่มสมาชิก (VIP Tier System)
 */

// ประเภทเงื่อนไขการเลื่อนขั้น
export enum ConditionType {
  MANUAL = 'manual',           // Admin เลื่อนขั้นเอง
  DEPOSIT_TOTAL = 'deposit_total',     // ยอดฝากสะสม
  TURNOVER_TOTAL = 'turnover_total',   // ยอดเทิร์นสะสม
  PROFIT_TOTAL = 'profit_total',       // กำไรสะสม (ฝาก - ถอน)
}

// สถานะกลุ่ม
export enum GroupStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

// ประเภทเกมสำหรับ Lossback
export const GAME_TYPES_FOR_LOSSBACK = [
  { key: 'slot', label: 'สล็อต' },
  { key: 'casino', label: 'คาสิโน' },
  { key: 'sport', label: 'กีฬา' },
  { key: 'lotto', label: 'หวย' },
  { key: 'card', label: 'ไพ่' },
  { key: 'esport', label: 'อีสปอร์ต' },
  { key: 'fishing', label: 'ยิงปลา' },
  { key: 'other', label: 'อื่นๆ' },
] as const

export type GameTypeKey = typeof GAME_TYPES_FOR_LOSSBACK[number]['key']

// การตั้งค่าคืนยอดเสียแต่ละประเภทเกม
export interface LossbackSettings {
  slot?: number      // % คืนยอดเสียสล็อต
  casino?: number    // % คืนยอดเสียคาสิโน
  sport?: number     // % คืนยอดเสียกีฬา
  lotto?: number     // % คืนยอดเสียหวย
  card?: number      // % คืนยอดเสียไพ่
  esport?: number    // % คืนยอดเสียอีสปอร์ต
  fishing?: number   // % คืนยอดเสียยิงปลา
  other?: number     // % คืนยอดเสียอื่นๆ
}

// สิทธิพิเศษ
export const AVAILABLE_BENEFITS = [
  { key: 'birthday_bonus', label: 'โบนัสวันเกิด', icon: 'cake' },
  { key: 'dedicated_support', label: 'ผู้ดูแลส่วนตัว', icon: 'headset' },
  { key: 'priority_withdraw', label: 'ถอนเงินด่วน', icon: 'zap' },
  { key: 'exclusive_promo', label: 'โปรโมชั่นพิเศษ', icon: 'gift' },
  { key: 'higher_limit', label: 'วงเงินสูงขึ้น', icon: 'trending-up' },
  { key: 'cashback_weekly', label: 'คืนเงินรายสัปดาห์', icon: 'refresh-cw' },
  { key: 'free_spin', label: 'ฟรีสปิน', icon: 'rotate-cw' },
  { key: 'special_event', label: 'งานอีเวนท์พิเศษ', icon: 'star' },
] as const

export type BenefitKey = typeof AVAILABLE_BENEFITS[number]['key']

// Interface หลักสำหรับ MemberGroup
export interface MemberGroup {
  id: string
  objectId: string
  
  // ข้อมูลพื้นฐาน
  name: string              // ชื่อกลุ่ม
  code: string              // รหัสกลุ่ม (unique)
  level: number             // ลำดับขั้น (1=ต่ำสุด)
  color: string             // สีประจำกลุ่ม
  icon: string              // ไอคอน
  description?: string      // คำอธิบาย
  
  // การตั้งค่า
  is_default: boolean       // เป็นกลุ่มเริ่มต้นสำหรับสมาชิกใหม่
  is_manual: boolean        // ต้อง Manual เลื่อนขั้นเท่านั้น
  
  // เงื่อนไขการเลื่อนขั้นอัตโนมัติ
  condition_type: ConditionType
  condition_amount: number  // ยอดที่ต้องถึงเพื่อเลื่อนขั้น
  
  // การตั้งค่าคืนยอดเสีย
  lossback_settings: LossbackSettings
  
  // การตั้งค่าฝาก/ถอน
  deposit_min: number       // ฝากขั้นต่ำ
  deposit_max: number       // ฝากสูงสุด/ครั้ง
  withdraw_max_daily: number // ถอนสูงสุด/วัน
  
  // สิทธิพิเศษ
  benefits: BenefitKey[]
  
  // สถิติ
  member_count: number      // จำนวนสมาชิกในกลุ่ม
  
  // สถานะ
  status: GroupStatus
  
  // Timestamps
  createdAt: string
  updatedAt: string
}

// Form data สำหรับสร้าง/แก้ไข
export interface MemberGroupFormData {
  name: string
  code: string
  level: number
  color: string
  icon: string
  description?: string
  is_default: boolean
  is_manual: boolean
  condition_type: ConditionType
  condition_amount: number
  lossback_settings: LossbackSettings
  deposit_min: number
  deposit_max: number
  withdraw_max_daily: number
  benefits: BenefitKey[]
  status: GroupStatus
}

// Query params
export interface MemberGroupQueryParams {
  query?: string
  status?: GroupStatus
  page?: number
  limit?: number
}

// สถิติรวม
export interface MemberGroupStats {
  total: number
  active: number
  inactive: number
  totalMembers: number
}

// สีเริ่มต้นสำหรับแต่ละระดับ
export const DEFAULT_GROUP_COLORS = [
  { level: 1, name: 'Bronze', color: '#CD7F32' },
  { level: 2, name: 'Silver', color: '#C0C0C0' },
  { level: 3, name: 'Gold', color: '#FFD700' },
  { level: 4, name: 'Platinum', color: '#E5E4E2' },
  { level: 5, name: 'Diamond', color: '#B9F2FF' },
  { level: 6, name: 'VIP', color: '#8B0000' },
] as const









